<template>
    <div class="row">
        <go-lotus-side-menu
            :side-menu-items="menuItems"
            @set-parent-prop="propSetFromChild($event)"
        >
        </go-lotus-side-menu>

        <div class="col-12 col-md-10 main-content position-relative p-3 mb-5">
            <div class="position-relative w-100">
                <div class="w-100 d-flex justify-content-between align-items-center">
                    <h4 class="py-3">Reports</h4>
                </div>
            </div>

            <report-module
                v-for="report in reports"
                :key="report.name"
                :report="report"
                @generate-report="generateReport(report.name, $event)"
            ></report-module>
        </div>
    </div>
</template>

<script>
import GoLotusSideMenu from "./GoLotusSideMenu";
import ReportModule from "./reports/ReportModule";
import { sideMenuItems } from "../plugins/sideMenuItems";
import { fixDatePickers } from "../plugins/fixDatePickers";
import { helpers } from "../plugins/helpers";

export default {
    name: "Reports",
    props: [
        "user",
        "organization",
        "fundingSources",
        "organizationPatients",
        "organizationTherapists",
    ],
    mixins: [sideMenuItems, fixDatePickers],
    components: {
        GoLotusSideMenu,
        ReportModule,
    },

    computed: {
        patientField() {
            return {
                name: "Patient",
                prop: "patient_id",
                option_null: "All patients",
                option_value: "id",
                option_title: "name",
                options: this.organizationPatients,
            };
        },
        therapistField() {
            return {
                name: "Therapist",
                prop: "therapist_id",
                option_null: "All therapists",
                option_value: "id",
                option_title: "name",
                options: this.organizationTherapists,
            };
        },
        appointmentStatusField() {
            return {
                name: "Appointment status",
                prop: "appointment_status",
                option_null: false,
                option_value: "status",
                option_title: "description",
                options: [
                    { status: "all", description: "All" },
                    { status: "completed", description: "Completed" },
                ],
            };
        },

        reports() {
            const billing = {
                name: "billing",
                title: "Billing",
                outputs: "Patient, Therapist, Funding Source, Amount billed, Amount paid",
                fields: [{ ...this.patientField }, { ...this.therapistField }],
                range: true,
            };

            const appointment = {
                name: "appointment",
                title: "Appointment + Billing",
                outputs:
                    "Appointment, Patient, Therapist, Rates, Codes, Funding Source, Amount billed",
                fields: [{ ...this.patientField }, { ...this.therapistField }],
                range: true,
            };

            const audit = {
                name: "audit",
                title: "Audit",
                outputs:
                    "Model, User ID, Organization ID, Action, Audit Type, Audit ID, Old Values, New Values, URL, IP Address, User Agent, Tags, Timestamps",
                fields: [],
                range: true,
            };

            const payroll = {
                name: "payroll",
                title: "Payroll Hours",
                outputs: "Therapist ID, Therapist Name, Payroll Type, Total Hours",
                fields: [{ ...this.appointmentStatusField }],
                range: true,
            };

            const expiredFundingSources = {
                name: "expired_patient_contracts",
                title: "Expired Funding Sources",
                outputs: "Patient name, Funding Source type & name, Contract dates",
                fields: [],
                range: false,
            };

            const expiredCredentials = {
                name: "expired_credentials",
                title: "Expired Credentials",
                outputs:
                    "Staff name, Credential type, degree & description, Issue and expiration dates and notes ",
                fields: [],
                range: false,
            };

            const cancelledAppointments = {
                name: "cancelled_appointments",
                title: "Cancelled Appointments",
                outputs:
                    "Appointment Type, Patient, Therapist, Appointment Date, Appointment Time, Cancel Reason",
                fields: [{ ...this.patientField }, { ...this.therapistField }],
                range: true,
            };

            const soapNotes = {
                name: "soap_notes",
                title: "Patient SOAP Notes",
                outputs: "Outputs: Session Date, Assessment, Objective, Subjective, Plan, Signed",
                fields: [],
                range: true,
            };

            const goldReports = [
                audit,
                payroll,
                expiredCredentials,
                cancelledAppointments,
                soapNotes,
            ];

            const platinumReports = [billing, appointment, expiredFundingSources];

            return [
                ...(this.organization.type === "professional_platinum" ? platinumReports : []),
                ...goldReports,
            ];
        },
    },

    methods: {
        generateReport(report, parameters) {
            helpers.toggleSpinner(`download-${report}-report`);
            let query = "?";
            Object.keys(parameters).forEach(key => {
                if (parameters[key]) {
                    let val = parameters[key];
                    if (key.includes("date")) {
                        val = this.moment(val).format("YYYY-MM-DD");
                    }
                    query += `${key}=${val}&`;
                }
            });
            axios({
                url: `/api/organizations/${this.organization.id}/reports/${report}${query}`,
                method: "GET",
                responseType: "blob",
            })
                .then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute(
                        "download",
                        `Export ${report} ${this.moment().format("YYYY-MM-DD")}.xlsx`
                    );
                    document.body.appendChild(link);
                    link.click();
                    helpers.toggleSpinner(`download-${report}-report`);
                })
                .catch(e => {
                    console.error(e);
                    const reader = new FileReader();
                    reader.addEventListener("loadend", e => {
                        const errorData = JSON.parse(e.srcElement.result);
                        this.$swal("Error", _.get(errorData, "message", {}), "error");
                    });
                    reader.readAsText(e.response.data);
                    helpers.toggleSpinner(`download-${report}-report`);
                });
        },
    },
};
</script>

<style scoped></style>
